import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, Clock, CheckCircle2, X, Info } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { DjayAvatar, useDjaySelection } from '../mascot';
interface FluencyIntroProps {
  isOpen: boolean;
  onStart: () => void;
  onCancel: () => void;
  isColdStart?: boolean;
}
export function FluencyIntro({
  isOpen,
  onStart,
  onCancel,
  isColdStart
}: FluencyIntroProps) {
  const {
    selectedVariant
  } = useDjaySelection();
  return <AnimatePresence data-id="element-2169">
      {isOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-2170">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onCancel} data-id="element-2171" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden" data-id="element-2172">
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-400 to-orange-400 p-6 text-white text-center relative" data-id="element-2173">
              <button onClick={onCancel} className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors" data-id="element-2174">
                <X className="w-5 h-5" data-id="element-2175" />
              </button>
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3 backdrop-blur-md" data-id="element-2176">
                <Zap className="w-8 h-8 text-white fill-white" data-id="element-2177" />
              </div>
              <h2 className="text-2xl font-black tracking-tight" data-id="element-2178">
                Snel & Zeker
              </h2>
            </div>

            {/* Content */}
            <div className="p-6 sm:p-8" data-id="element-2179">
              <div className="flex gap-4 items-start mb-6" data-id="element-2180">
                <div className="flex-shrink-0 mt-1" data-id="element-2181">
                  <DjayAvatar size="md" emotion="excited" variant={selectedVariant} animate={true} data-id="element-2182" />
                </div>
                <div className="bg-amber-50 rounded-2xl rounded-tl-none p-4 border border-amber-100 text-amber-900 text-sm font-medium leading-relaxed" data-id="element-2183">
                  Hoi! Bij Snel & Zeker oefen je dingen die je al kent. Het doel
                  is om ze steeds sneller goed te krijgen!
                </div>
              </div>

              {/* Cold-start info banner */}
              {isColdStart && <div className="flex items-start gap-3 p-3 mb-6 bg-amber-50 border border-amber-200 rounded-xl" data-id="element-2184">
                  <div className="mt-0.5 bg-amber-100 p-1 rounded-full text-amber-600 flex-shrink-0" data-id="element-2185">
                    <Info className="w-4 h-4" data-id="element-2186" />
                  </div>
                  <p className="text-xs text-amber-800 font-medium leading-relaxed" data-id="element-2187">
                    Snel & Zeker werkt het beste als je al wat geoefend hebt. Nu
                    krijg je een goede startset om mee te beginnen!
                  </p>
                </div>}

              <div className="space-y-4 mb-8" data-id="element-2188">
                <div className="flex items-start gap-3" data-id="element-2189">
                  <div className="mt-0.5 bg-emerald-100 p-1 rounded-full text-emerald-600" data-id="element-2190">
                    <CheckCircle2 className="w-4 h-4" data-id="element-2191" />
                  </div>
                  <div data-id="element-2192">
                    <p className="text-sm font-bold text-slate-800" data-id="element-2193">
                      Bekende stof
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-2194">
                      Je krijgt vragen die je al eerder goed hebt beantwoord.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-2195">
                  <div className="mt-0.5 bg-blue-100 p-1 rounded-full text-blue-600" data-id="element-2196">
                    <Clock className="w-4 h-4" data-id="element-2197" />
                  </div>
                  <div data-id="element-2198">
                    <p className="text-sm font-bold text-slate-800" data-id="element-2199">
                      Geen tijdslimiet
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-2200">
                      Doe het op jouw eigen tempo. Er is geen stress!
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-2201">
                  <div className="mt-0.5 bg-amber-100 p-1 rounded-full text-amber-600" data-id="element-2202">
                    <Zap className="w-4 h-4" data-id="element-2203" />
                  </div>
                  <div data-id="element-2204">
                    <p className="text-sm font-bold text-slate-800" data-id="element-2205">
                      Snelheidsmeting
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-2206">
                      Na afloop zie je hoe snel je gemiddeld was.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3" data-id="element-2207">
                <Button variant="secondary" onClick={onCancel} className="flex-1" data-id="element-2208">
                  Niet nu
                </Button>
                <Button variant="primary" onClick={onStart} className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 border-none shadow-lg shadow-amber-200" data-id="element-2209">
                  Start!
                </Button>
              </div>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}